<template>
  <section class="custom-launch-pad">
    <div class="row custom-header">
      <div class="col">
        <h5 class="mb-0">
          LaunchPad
          <a href="#" class="custom-refresh" title="Look the LaunchPad matrix up again" @click.prevent="refresh">
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': isLoading }"></i>
          </a>
        </h5>
      </div>
    </div>
    <div class="custom-body">
      <div v-if="isLoading" class="row mt-3">
        <div class="col text-center"><i class="fas fa-spinner fa-spin"></i></div>
      </div>
      <div v-else-if="!records.length && !groupRecord" class="row mt-3">
        <div class="col">
          <div class="alert alert-info mb-0">
            <div>{{ noMatchMessage }}</div>
            <div class="mt-2">Searched LaunchPad matrix for:</div>
            <ul class="mb-0 mt-1">
              <li v-for="(part, index) in searchedFor" :key="index"><strong>{{ part.label }}:</strong> {{ part.value || '(none)' }}</li>
            </ul>
          </div>
        </div>
      </div>
      <template v-else>
        <div class="custom-tab-panel">
          <div v-if="!records.length" class="alert alert-info p-2 mb-2">
            {{ noMatchMessage }} Add a record below to create one.
          </div>
          <!-- Reported Vehicle -->
          <div class="custom-info-row row border mb-3">
            <div class="custom-info-title">Reported Vehicle</div>
            <div class="row custom-info-row">
              <div class="col-5"><i class="fas fa-car-side fa-fw custom-info-icon"></i>Make</div>
              <div class="col-7 custom-info-value">{{ reportedMake }}</div>
            </div>
            <div class="row custom-info-row">
              <div class="col-5"><i class="fas fa-car fa-fw custom-info-icon"></i>Model</div>
              <div class="col-7 custom-info-value">{{ reportedModel }}</div>
            </div>
            <div class="row custom-info-row">
              <div class="col-5"><i class="fas fa-calendar-alt fa-fw custom-info-icon"></i>Year</div>
              <div class="col-7 custom-info-value">{{ reportedYear }}</div>
            </div>
          </div>
          <!-- the same shape the make matrix Links tab has: the vehicle's link ids on the
               left, and the links held against whichever one is selected on the right -->
          <div class="row mt-2">
            <div class="col-12 col-xl-5">
              <div v-if="canAddRecord" class="row">
                <div class="w-100">
                  <span class="mb-1">
                    ** To add a record to <strong>{{ selectedGroupName || 'this group' }}</strong>, choose its year and enter its model,
                    then press Add. A record is staged for each link type - System, Location, Static and Dynamic - and any the group
                    already holds are left out.
                  </span>
                  <div class="input-group input-group-sm">
                    <span class="input-group-text">Year</span>
                    <select v-model="newRecord.year" class="form-control" aria-label="new record year">
                      <option value="">all years</option>
                      <option v-for="year of newRecordYears" :key="year" :value="year">{{ year }}</option>
                    </select>
                    <span class="input-group-text">Model</span>
                    <input
                      v-model="newRecord.model"
                      type="text"
                      class="form-control"
                      placeholder="model"
                      aria-label="new record model"
                      @keyup.enter.prevent="addRecord"
                    />
                    <button type="button" class="btn btn-primary" tabindex="-1" @click="addRecord">Add</button>
                  </div>
                  <div v-if="newRecordMessage" class="row">
                    <div class="col text-danger">{{ newRecordMessage }}</div>
                  </div>
                </div>
              </div>
              <div class="row mt-2">
                <div class="col">
                  <span>ADAS Links ({{ visibleRecords.length }}):</span>
                </div>
                <div class="col-auto">
                  <input
                    type="checkbox"
                    class="custom-link-check"
                    title="check all records"
                    aria-label="check all records"
                    :checked="checkAllRecords"
                    @change="checkAllVisibleRecords($event.target.checked)"
                  />
                </div>
              </div>
              <div class="custom-link-ids-wrapper mt-2">
                <div v-if="!visibleRecords.length" class="p-2 text-center text-muted">No records for the selected year.</div>
                <ul class="custom-link-ids">
                  <li
                    v-for="item of visibleRecords"
                    :key="recordKey(item)"
                    class="custom-link-id"
                    :class="{
                      'custom-link-id-selected': isSelected(item),
                      'custom-link-id-new': isPendingRecord(item),
                      'custom-link-id-removed': isRecordDeleted(item),
                      'pe-none opacity-50': isSaving
                    }"
                    @click="chooseRecord(item)"
                  >
                    <div class="d-flex justify-content-between align-items-start">
                      <div>
                        <span :class="{ 'custom-deleted-title': isRecordDeleted(item) }">{{ item.year }} {{ item.model }}</span>
                        <span v-if="isPendingRecord(item)" class="ms-2 badge bg-success">New</span>
                        <span v-if="hasUnsavedLinks(item)" class="ms-2 badge bg-warning text-dark">Unsaved</span>
                        <span v-if="isRecordDeleted(item)" class="ms-2 badge bg-danger">Removed on save</span>
                      </div>
                      <div class="custom-row-actions" @click.stop>
                        <input
                          v-if="!isPendingRecord(item) && !isRecordDeleted(item)"
                          type="checkbox"
                          class="custom-link-check"
                          title="check to remove"
                          aria-label="check to remove"
                          :checked="isRecordChecked(item)"
                          @change="toggleRecordChecked(item)"
                        />
                        <button
                          v-if="isPendingRecord(item)"
                          type="button"
                          class="btn btn-link btn-sm text-danger"
                          title="Remove record"
                          aria-label="Remove record"
                          @click="removeAddedRecord(item)"
                        >
                          <i class="fas fa-times"></i>
                        </button>
                        <button
                          v-else-if="!isRecordDeleted(item)"
                          type="button"
                          class="btn btn-link btn-sm text-danger"
                          title="Remove record"
                          aria-label="Remove record"
                          @click="removeStoredRecord(item)"
                        >
                          <i class="fas fa-times"></i>
                        </button>
                        <button
                          v-else
                          type="button"
                          class="btn btn-link btn-sm text-secondary"
                          title="Undo remove"
                          aria-label="Undo remove"
                          @click="restoreRecord(item)"
                        >
                          <i class="fas fa-undo"></i>
                        </button>
                      </div>
                    </div>
                    <div class="custom-link-id-detail">{{ recordDescription(item) }}</div>
                    <div>
                      <span class="custom-year-model">{{ recordLabel(item) }}</span>
                    </div>
                  </li>
                </ul>
              </div>
              <div class="row mt-1">
                <div class="col-auto">
                  <button
                    type="button"
                    class="btn btn-sm"
                    :class="checkedRemovableRecords.length ? 'btn-danger' : 'btn-secondary'"
                    :disabled="!checkedRemovableRecords.length"
                    @click="removeCheckedRecords"
                  >
                    Remove checked
                  </button>
                </div>
              </div>
            </div>
            <!-- and what is held against the selected one -->
            <div class="col">
              <div v-if="isLoadingRecord" class="row mt-3">
                <div class="col text-center"><i class="fas fa-spinner fa-spin"></i></div>
              </div>
              <div v-else-if="!record" class="row mt-3">
                <div class="col text-center text-muted">
                  {{ visibleRecords.length ? 'Select an ADAS Links record to see the links it holds.' : 'No ADAS Links records for the selected year.' }}
                </div>
              </div>
              <template v-else>
                <!-- Master Links -->
                <div class="row">
                  <div class="col">
                    <div v-if="isPendingSelected" class="alert alert-success p-2 mt-1 mb-0">
                      <strong>{{ record.year }} {{ record.model }} - {{ lookupTypeName(record.lookupType) }}</strong> is not in the matrix
                      yet. It and the links below are created when you press Save.
                    </div>
                    <h6 class="mt-1">Links<span class="text-muted">({{ totalLinks }})</span>
                    </h6>
                    <div class="row">
                      <div class="w-100">
                        <span class="mb-1">
                          ** To add a link to this record, enter its title and url, then press Add. You can create a hierarchical
                          structure using the >> characters in your title.
                        </span>
                        <div class="input-group input-group-sm">
                          <input
                            v-model="newLink.name"
                            type="text"
                            class="form-control"
                            placeholder="new link name"
                            aria-label="new link name"
                            @keyup.enter.prevent="addLink"
                          />
                          <input
                            v-model="newLink.url"
                            type="text"
                            class="form-control"
                            placeholder="new link url"
                            aria-label="new link url"
                            @keyup.enter.prevent="addLink"
                          />
                          <button type="button" class="btn btn-primary" tabindex="-1" @click="addLink">Add</button>
                        </div>
                        <div v-if="newLinkMessage" class="row">
                          <div class="col text-danger">{{ newLinkMessage }}</div>
                        </div>
                      </div>
                    </div>
                    <div v-if="!linkGroups.length" class="row mt-3">
                      <div class="col text-center">This record holds no links.</div>
                    </div>
                    <div v-for="(group, groupIdx) in linkGroups" :key="groupIdx" class="row border m-0 mb-2 mt-2">
                      <div class="col">
                        <div class="row custom-collapse-header" @click="toggleGroup(groupIdx)">
                          <div class="col">
                            <i class="fa" :class="isExpanded(groupIdx) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                            <span class="ms-2">{{ group.group || '(no group)' }}</span>
                            <span class="ms-2 text-muted">({{ (group.links || []).length }})</span>
                          </div>
                          <div class="col-auto custom-row-actions" @click.stop>
                            <button
                              v-if="hasRemovableLinks(group)"
                              type="button"
                              class="btn btn-link btn-sm text-danger"
                              title="Remove every link in this group"
                              aria-label="Remove all links in this group"
                              @click="removeGroup(group)"
                            >
                              Remove All
                              <!-- <i class="fas fa-times"></i> -->
                            </button>
                            <button
                              v-if="hasRestorableLinks(group)"
                              type="button"
                              class="btn btn-link btn-sm text-secondary"
                              title="Undo remove for this group"
                              aria-label="Undo removal for this group"
                              @click="restoreGroup(group)"
                            >
                              Undo
                              <!-- <i class="fas fa-undo"></i> -->
                            </button>
                          </div>
                        </div>
                        <div v-show="isExpanded(groupIdx)">
                          <div
                            v-for="(link, linkIdx) in group.links"
                            :key="linkIdx"
                            class="row custom-master-link"
                            :class="{
                              'custom-reported-link': isReported(link),
                              'custom-changed-link': isChanged(link),
                              'custom-deleted-link': isDeleted(link)
                            }"
                          >
                            <div class="col">
                              <div class="row custom-collapse-header" @click="toggleLink(link)">
                                <div class="col">
                                  <i class="fa fa-fw" :class="isLinkExpanded(link) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                                  <span class="ms-2" :class="{ 'custom-deleted-title': isDeleted(link) }">
                                    {{ documentTitle(link) || linkUrl(link) }}
                                  </span>
                                  <span v-if="isReported(link)" class="ms-2 badge bg-danger">reported broken</span>
                                  <span v-if="link.isNew" class="ms-2 badge bg-success">New</span>
                                  <span v-if="isDeleted(link)" class="ms-2 badge bg-danger">Removed on save</span>
                                </div>
                                <!-- removing a row doesn't need the row open, so the action sits in the header -->
                                <div class="col-auto custom-row-actions" @click.stop>
                                  <button
                                    v-if="link.isNew"
                                    type="button"
                                    class="btn btn-link btn-sm text-danger"
                                    title="Remove link"
                                    aria-label="Remove link"
                                    @click="removeAddedLink(link)"
                                  >
                                    <i class="fas fa-times"></i>
                                  </button>
                                  <button
                                    v-else-if="!isDeleted(link)"
                                    type="button"
                                    class="btn btn-link btn-sm text-danger"
                                    title="Remove link"
                                    aria-label="Remove link"
                                    @click="removeStoredLink(link)"
                                  >
                                    <i class="fas fa-times"></i>
                                  </button>
                                  <button
                                    v-else
                                    type="button"
                                    class="btn btn-link btn-sm text-secondary"
                                    title="Undo remove"
                                    aria-label="Undo remove"
                                    @click="restoreStoredLink(link)"
                                  >
                                    <i class="fas fa-undo"></i>
                                  </button>
                                </div>
                              </div>
                              <div v-show="isLinkExpanded(link)">
                                <div class="row mt-2">
                                  <div class="col">
                                    <div class="input-group input-group-sm">
                                      <span class="input-group-text">Title</span>
                                      <input
                                        :value="documentTitle(link)"
                                        type="text"
                                        class="form-control"
                                        :readonly="isDeleted(link)"
                                        @input="documentTitleChanged(link, $event.target.value)"
                                      />
                                    </div>
                                  </div>
                                </div>
                                <div class="row mt-2 mb-2">
                                  <div class="col">
                                    <div class="input-group input-group-sm">
                                      <span class="input-group-text">Url</span>
                                      <input
                                        :value="linkUrl(link)"
                                        type="text"
                                        class="form-control"
                                        :readonly="isDeleted(link)"
                                        @input="urlChanged(link, $event.target.value)"
                                      />
                                      <a
                                        v-if="linkUrl(link)"
                                        class="btn btn-outline-secondary"
                                        :href="linkUrl(link)"
                                        title="open link"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <i class="fa fa-external-link-alt"></i>
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
          <div class="row custom-save-bar mt-2 align-items-center">
            <div class="col">
              <!-- <span v-if="hasChanges" class="text-danger">{{ changeSummary }}</span> -->
              <span v-if="saveMessage" :class="isSaveError ? 'text-danger' : 'text-success'" class="ms-2">{{ saveMessage }}</span>
            </div>
            <div class="col-auto">
              <button type="button" class="btn btn-secondary btn-sm me-2" :disabled="isSaving" @click="cancelChanges">Cancel</button>
              <button type="button" class="btn btn-danger btn-sm" :disabled="isSaving || !hasChanges" @click="save">
                <i v-if="isSaving" class="fas fa-spinner fa-spin me-1"></i>Save
              </button>
              <button
                type="button"
                class="btn btn-primary btn-sm ms-2"
                :disabled="isSaving || hasChanges || !canOpenPush"
                :title="pushBtnTitle"
                @click="openPush">
                Push
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>

    <dialog-repair-link-push v-model="pushRecords" :system="reportedSystem" @closing="closePush" />

    <confirm-action
      v-model="showDiscardConfirm"
      confirm-title="Unsaved Changes"
      confirm-action="Discard"
      confirm-question="There are unsaved changes to the LaunchPad records and links. Are you sure you want to discard them?"
      cancel-action="Keep Editing"
      @closing="cancelDiscard"
      @proceed="confirmDiscard"
    />
  </section>
</template>
<script>
import _ from 'lodash'
import { mapState, mapActions } from 'pinia'
import { adasmatrixStore } from '@/stores/adasmatrix'
import ConfirmAction from '@/components/utilities/elements/ConfirmDialog.vue'
import DialogRepairLinkPush from './dialog-repair-link-push.vue'
import {
  applyLinkChangesDetailed,
  buildLinkGroups,
  buildManualLink,
  findTargetGroupIndex,
  DEFAULT_GROUP_CATEGORY_NAME,
  ADASLINKS_TYPES,
  findLaunchPadVehicleLinks,
  normalize,
  isMakeMatrix
} from '@/plugins/broken-link-helper'

export default {
  name: 'RepairLinkLaunchPadDetail',
  components: {
    ConfirmAction,
    DialogRepairLinkPush
  },
  props: {
    model: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      isLoading: false,
      isLoadingRecord: false,
      // the vehicle's AdasLinks listing rows, and the full record for the selected one
      records: [],
      selectedId: null,
      // a staged record has no id, so the selection falls back to its key
      selectedPendingKey: null,
      record: null,
      // the last record loaded, which names the group Add stages into while no record is selected
      groupRecord: null,
      noMatchMessage: '',
      expandedGroups: [],
      // a lookup for a report the admin has already moved on from must not land
      requestId: 0,
      recordRequestId: 0,
      // staged edits to the selected record's links, applied when this column is saved
      addedLinks: [],
      deletedLinks: [],
      documentTitles: {},
      editedUrls: {},
      // link edits of records the admin moved off without saving, by AdasLinks id -- each holds
      // the record as loaded and its staged edits, under the same names as the selected record's
      stashedEdits: {},
      // the selected record's other link types as loaded, by AdasLinks id, so a change can be
      // staged on them as it is made
      siblingCache: {},
      // ties a link added here to its copies on the other link types
      mirrorKeySeed: 0,
      expandedLinks: {},
      newLink: { name: '', url: '' },
      newLinkMessage: '',
      // staged record changes, applied when this column is saved. These are panel wide,
      // where the link edits above belong to whichever record is selected
      addedRecords: [],
      deletedRecords: [],
      checkedRecords: [],
      pendingKeySeed: 0,
      newRecord: { year: '', model: '' },
      newRecordMessage: '',
      saveMessage: '',
      isSaveError: false,
      isSaving: false,
      // what to do once the admin has answered the unsaved-changes confirm
      showDiscardConfirm: false,
      pendingAction: null,
      pushRecords: null
    }
  },
  async mounted() {
    await this.load()
  },
  watch: {
    model() {
      // moving to another report reloads the panel, so warn before the edits go with it
      this.guardAll(() => this.load())
    },
    'newRecord.year'() {
      this.checkedRecords = []
      this.selectVisibleRecord()
    },
    record(value) {
      if (value) {
        this.groupRecord = value
      }
    }
  },
  computed: {
    ...mapState(adasmatrixStore, ['lookups']),
    reportedMake() {
      return (this.model && this.model.vehicleMakeName) || ''
    },
    reportedModel() {
      return (this.model && (this.model.matrixModel || this.model.vehicleModel)) || ''
    },
    reportedYear() {
      return (this.model && this.model.vehicleYear) || ''
    },
    reportedSystem() {
      return (this.model && this.model.system) || ''
    },
    visibleRecords() {
      let year = parseInt(this.newRecord.year)
      if (!year) {
        return this.allRecords
      }
      return this.allRecords.filter((row) => parseInt(row.year) === year)
    },
    allRecords() {
      return this.records.concat(this.addedRecords)
    },
    /** The record a new one is added alongside: the selected one, or the last one loaded. */
    groupSource() {
      return this.record || this.groupRecord
    },
    /** The group a new record joins, named the way the listing rows name theirs. */
    selectedGroupName() {
      if (!this.groupSource) {
        return ''
      }
      return [this.groupSource.adasChecklistMatrixName, this.groupSource.system, this.groupSource.location]
        .filter((part) => part)
        .join(' / ')
    },
    /** A record can only be added to a group, and the loaded record is what names one. */
    canAddRecord() {
      return !!this.groupSource
    },
    selectedPendingRecord() {
      if (!this.selectedPendingKey) {
        return null
      }
      return this.addedRecords.find((row) => row.pendingKey === this.selectedPendingKey) || null
    },
    isPendingSelected() {
      return !!this.selectedPendingRecord
    },
    /** Links typed against records that are not created yet, counted for the save bar. */
    pendingLinkCount() {
      return this.addedRecords.reduce((total, row) => total + (row.stagedLinks || []).length, 0)
    },
    /** The years the add offers: this vehicle's matrix range. */
    newRecordYears() {
      let years = []
      let start = parseInt(this.groupSource && this.groupSource.yearStart)
      let end = parseInt(this.groupSource && this.groupSource.yearEnd)
      if (start && end && end >= start) {
        for (let year = start; year <= end; year++) {
          years.push(year)
        }
      }
      // the reported year can fall outside the range, and that is exactly when the matrix
      // is missing the record this panel is here to add
      let reported = parseInt(this.reportedYear)
      if (reported && years.indexOf(reported) === -1) {
        years.push(reported)
        years.sort((left, right) => left - right)
      }
      return years
    },
    /** What the record holds, before any of the admin's staged edits. */
    storedGroups() {
      return this.record ? buildLinkGroups(this.record.links) : []
    },
    linkGroups() {
      let groups = this.storedGroups.map((group) => ({ ...group, links: (group.links || []).slice() }))
      if (!this.addedLinks.length) {
        return groups
      }
      // resolve each added link exactly the way the save will, seeding a group here
      // too when it finds no home -- otherwise the admin is shown one group and the
      // record is written with another
      let descriptors = this.rawGroups()
      this.addedLinks.forEach((link) => {
        let idx = findTargetGroupIndex(descriptors, this.groupOptions, { categoryId: link.category })
        if (idx === -1) {
          let name = this.groupOptions.groupCategoryName || DEFAULT_GROUP_CATEGORY_NAME
          descriptors.push({ groupCategory: { id: this.groupOptions.groupCategoryId, name: name } })
          groups.push({ group: name, links: [] })
          idx = groups.length - 1
        }
        groups[idx].links.push(link)
      })
      return groups
    },
    /** The seed values a new group takes when the record holds no links yet. */
    groupOptions() {
      let options = this.groupOptionsFor(this.record)
      // a new ADAS Links record's links start in the default group
      if (this.isPendingSelected) {
        options.groupCategoryName = DEFAULT_GROUP_CATEGORY_NAME
      }
      return options
    },
    totalLinks() {
      return this.linkGroups.reduce((total, group) => total + (group.links || []).length, 0)
    },
    /** Edits to the selected record's links. A staged record keeps its links on itself. */
    hasSelectedLinkChanges() {
      return !this.isPendingSelected && !!this.record && this.buildChanges().length > 0
    },
    /** Link edits on the records the admin moved off, which the save writes too. */
    stashedChangeCount() {
      return Object.values(this.stashedEdits).reduce((total, edits) => total + this.buildChanges(edits).length, 0)
    },
    hasLinkChanges() {
      return this.hasSelectedLinkChanges || this.stashedChangeCount > 0
    },
    /** Records staged for creation or removal, which outlive the selection. */
    hasRecordChanges() {
      return this.addedRecords.length > 0 || this.deletedRecords.length > 0 || this.pendingLinkCount > 0
    },
    hasChanges() {
      return this.hasLinkChanges || this.hasRecordChanges
    },
    changeSummary() {
      let parts = []
      if (this.addedRecords.length) {
        parts.push(`${this.addedRecords.length} record(s) to add`)
      }
      if (this.pendingLinkCount) {
        parts.push(`${this.pendingLinkCount} link(s) on them`)
      }
      if (this.deletedRecords.length) {
        parts.push(`${this.deletedRecords.length} record(s) to remove`)
      }
      // a staged record's links are already counted above, so don't count them twice
      let links = (this.hasSelectedLinkChanges ? this.buildChanges().length : 0) + this.stashedChangeCount
      if (links) {
        parts.push(`${links} link change(s)`)
      }
      return parts.length ? `Unsaved: ${parts.join(', ')}.` : ''
    },
    /** The visible rows a removal can act on -- a staged or already-removed one is not among them. */
    removableRecords() {
      return this.visibleRecords.filter((row) => !this.isPendingRecord(row) && !this.isRecordDeleted(row))
    },
    /** Derived, so the check-all box can never disagree with the rows it stands for. */
    checkAllRecords() {
      return this.removableRecords.length > 0 && this.removableRecords.every((row) => this.isRecordChecked(row))
    },
    /** Ticked rows a removal can still act on, which is what enables the bulk button. */
    checkedRemovableRecords() {
      return this.visibleRecords.filter((row) => this.isRecordChecked(row) && !this.isRecordDeleted(row))
    },
    searchedFor() {
      const data = this.model || {}
      return [
        { label: 'Make', value: data.vehicleMakeName },
        { label: 'Model', value: data.vehicleModel || data.matrixModel },
        { label: 'Year', value: data.vehicleYear }
      ]
    },
    /** The url the customer reported, so the master copy of it can be called out. */
    reportedUrl() {
      return normalize(this.model ? this.model.url : '')
    },
    /**
     * Records a push can reach: the export filters on Year and Model, and a record that is
     * not saved yet -- or is on its way out -- is not there to be pushed at all.
     */
    pushableRecords() {
      return this.visibleRecords.filter(
        (row) => !this.isPendingRecord(row) && !this.isRecordDeleted(row) && row.year && normalize(row.model)
      )
    },
    canOpenPush() {
      return this.pushableRecords.length > 0
    },
    pushBtnTitle() {
      if (this.hasChanges) {
        return 'Save your changes before pushing'
      }
      if (!this.canOpenPush) {
        return this.visibleRecords.length
          ? 'These records have no year or model, so a push cannot reach them'
          : 'There are no records to push'
      }
      return 'Push these links to the destination(s)'
    }
  },
  methods: {
    ...mapActions(adasmatrixStore, [
      'getLookups',
      'fetchAdasMatrices',
      'fetchAdasMakeMatrix',
      'getLinkManage',
      'saveLinkManage',
      'saveAdasLinks',
      'removeAdasLinks'
    ]),
    lookupTypeName(lookupType) {
      let found = ADASLINKS_TYPES.find((x) => x.value === parseInt(lookupType))
      return found ? found.name : ''
    },
    /** What tells one of the vehicle's records from the next: its category, system and location. */
    recordDescription(row) {
      return [row.category, row.system, row.location].filter((part) => part).join(' / ')
    },
    /** The same matrix record, year and model under the other link types, each holding its own copy of the links. */
    siblingRecords(selected, removedIds) {
      if (!selected) {
        return []
      }
      return this.records.filter(
        (row) =>
          row.adasLinks &&
          parseInt(row.adasLinks) !== parseInt(selected.adasLinks) &&
          removedIds.indexOf(parseInt(row.adasLinks)) === -1 &&
          parseInt(row.lookupType) !== parseInt(selected.lookupType) &&
          parseInt(row.adasChecklistMatrixVehicle) === parseInt(selected.adasChecklistMatrixVehicle) &&
          parseInt(row.year) === parseInt(selected.year) &&
          normalize(row.model) === normalize(selected.model)
      )
    },
    /** A staged record has no id yet, so it needs a key of its own for the list. */
    recordKey(row) {
      return row && row.adasLinks ? `record-${row.adasLinks}` : (row && row.pendingKey) || 'record'
    },
    recordLabel(row) {
      return `#${row.adasLinks || '(new)'} - ${this.lookupTypeName(row.lookupType)}`
    },
    isPendingRecord(row) {
      return !!(row && row.isNew)
    },
    isRecordDeleted(row) {
      return !!(row && row.adasLinks) && this.deletedRecords.indexOf(parseInt(row.adasLinks)) !== -1
    },
    isRecordChecked(row) {
      return !!(row && row.adasLinks) && this.checkedRecords.indexOf(parseInt(row.adasLinks)) !== -1
    },
    isSelected(row) {
      if (this.isPendingRecord(row)) {
        return !!this.selectedPendingKey && row.pendingKey === this.selectedPendingKey
      }
      return !!row.adasLinks && parseInt(row.adasLinks) === parseInt(this.selectedId)
    },
    isExpanded(groupIdx) {
      return this.expandedGroups.indexOf(groupIdx) !== -1
    },
    toggleGroup(groupIdx) {
      let position = this.expandedGroups.indexOf(groupIdx)
      if (position === -1) {
        this.expandedGroups.push(groupIdx)
      } else {
        this.expandedGroups.splice(position, 1)
      }
    },
    isReported(link) {
      if (!this.reportedUrl || !link) {
        return false
      }
      return [link.url, link.href].some((value) => {
        let candidate = normalize(value)
        return candidate !== '' && (candidate === this.reportedUrl || this.reportedUrl.indexOf(candidate) !== -1)
      })
    },
    refresh() {
      if (!this.isLoading) {
        this.guardAll(() => this.load())
      }
    },
    linkRef(link) {
      return link ? String(link.href || link.url || '') : ''
    },
    rawGroups() {
      return this.storedGroups.map((group) => ({
        groupCategory: {
          id: (group.links || []).length ? group.links[0].category : null,
          name: group.group || ''
        }
      }))
    },
    isLinkExpanded(link) {
      return this.expandedLinks[this.linkRef(link)] === true
    },
    toggleLink(link) {
      this.expandedLinks[this.linkRef(link)] = !this.isLinkExpanded(link)
    },
    documentTitle(link, edits = this) {
      if (link.isNew) {
        return link.name || ''
      }
      let key = this.linkRef(link)
      return edits.documentTitles[key] === undefined ? link.name || link.article || '' : edits.documentTitles[key]
    },
    documentTitleChanged(link, value) {
      if (link.isNew) {
        link.name = value
        link.article = value
        this.mirrorAddedChange(link, { name: value, article: value })
      } else {
        this.documentTitles[this.linkRef(link)] = value
        this.mirrorEdit(this.linkRef(link), 'documentTitles', value)
      }
      this.clearSaveMessage()
    },
    linkUrl(link, edits = this) {
      if (link.isNew) {
        return link.url || ''
      }
      let key = this.linkRef(link)
      return edits.editedUrls[key] === undefined ? link.url || '' : edits.editedUrls[key]
    },
    urlChanged(link, value) {
      if (link.isNew) {
        link.url = value
        link.href = value
        this.mirrorAddedChange(link, { url: value, href: value })
      } else {
        this.editedUrls[this.linkRef(link)] = value
        this.mirrorEdit(this.linkRef(link), 'editedUrls', value)
      }
      this.clearSaveMessage()
    },
    hasUrl(url) {
      return this.linkGroups.some((group) => (group.links || []).some((link) => this.linkUrl(link) === url || this.linkRef(link) === url))
    },
    addLink() {
      let name = String(this.newLink.name || '').trim()
      let url = String(this.newLink.url || '').trim()
      this.newLinkMessage = ''
      if (!this.record) {
        this.newLinkMessage = 'Select an ADAS Links record first.'
        return
      }
      if (!name || !url) {
        this.newLinkMessage = 'Enter both a title and a url for the new link.'
        return
      }
      if (this.hasUrl(url)) {
        this.newLinkMessage = 'That url is already in this list.'
        return
      }
      let added = buildManualLink(name, url, this.record.adasChecklistMatrixVehicle)
      added.mirrorKey = ++this.mirrorKeySeed
      this.addedLinks.push(added)
      this.mirrorAdd(added)
      this.newLink = { name: '', url: '' }
      this.clearSaveMessage()
      // the new link is at the bottom of its group, so open the group it landed in
      this.$nextTick(() => {
        let idx = this.linkGroups.findIndex((group) => (group.links || []).some((link) => link.isNew && link.url === url))
        if (idx !== -1 && !this.isExpanded(idx)) {
          this.expandedGroups.push(idx)
        }
      })
    },
    removeAddedLink(link) {
      let position = this.addedLinks.indexOf(link)
      if (position === -1) {
        return
      }
      this.addedLinks.splice(position, 1)
      this.mirrorRemoveAdded(link)
      this.newLinkMessage = ''
      this.clearSaveMessage()
    },
    isDeleted(link, edits = this) {
      let ref = this.linkRef(link)
      return ref !== '' && edits.deletedLinks.indexOf(ref) !== -1
    },
    /** A stored link is only marked here; it leaves the payload when this column saves. */
    removeStoredLink(link) {
      let ref = this.linkRef(link)
      if (!link || link.isNew || ref === '' || this.isDeleted(link)) {
        return
      }
      this.deletedLinks.push(ref)
      this.mirrorRemove(ref, true)
      this.clearSaveMessage()
    },
    /** A group still holding something to take out -- a newly added link counts. */
    hasRemovableLinks(group) {
      return (group.links || []).some((link) => link.isNew || !this.isDeleted(link))
    },
    /** A group holding a marked link, so the undo is worth offering. */
    hasRestorableLinks(group) {
      return (group.links || []).some((link) => this.isDeleted(link))
    },
    /** Clearing a group is just its links removed one by one, so the staging rules stay the same. */
    removeGroup(group) {
      // a copy: removeAddedLink splices addedLinks, which rebuilds group.links mid-loop
      ;(group.links || []).slice().forEach((link) => {
        if (link.isNew) {
          this.removeAddedLink(link)
          return
        }
        this.removeStoredLink(link)
      })
    },
    /** Undoes the marks, but a link the admin had typed in is already gone by then. */
    restoreGroup(group) {
      ;(group.links || []).slice().forEach((link) => this.restoreStoredLink(link))
    },
    restoreStoredLink(link) {
      let position = this.deletedLinks.indexOf(this.linkRef(link))
      if (position === -1) {
        return
      }
      this.deletedLinks.splice(position, 1)
      this.mirrorRemove(this.linkRef(link), false)
      this.clearSaveMessage()
    },
    /**
     * A record is staged against the selected record's group. The listing rows carry no
     * AdasChecklistMatrixVehicle, so the loaded record is the only source for the ids the
     * save needs -- and taking its category, system and location with them is what keeps
     * the staged rows past the reported-system filter.
     *
     * One add stages a row per link type, because that is one AdasLinks record each. The
     * types this group already holds for the year and model are left out rather than sent
     * to be refused, so pressing Add again just fills in whatever is missing.
     */
    addRecord() {
      this.newRecordMessage = ''
      if (!this.groupSource) {
        this.newRecordMessage = 'Select an ADAS Links record first - the new record is added to its group.'
        return
      }
      let year = parseInt(String(this.newRecord.year || '').trim())
      let model = String(this.newRecord.model || '').trim()
      if (!year) {
        this.newRecordMessage = 'Choose a year for the new record.'
        return
      }
      if (!model) {
        this.newRecordMessage = 'Enter a model for the new record.'
        return
      }
      let missing = ADASLINKS_TYPES.filter(
        (type) =>
          !this.allRecords.some(
            (row) =>
              parseInt(row.year) === year &&
              normalize(row.model) === normalize(model) &&
              parseInt(row.lookupType) === type.value &&
              this.recordDescription(row) === this.selectedGroupName
          )
      )
      if (!missing.length) {
        this.newRecordMessage = 'That year and model is already in this group for every link type.'
        return
      }
      let seed = ++this.pendingKeySeed
      missing.forEach((type) => {
        this.addedRecords.push({
          pendingKey: `pending-${seed}-${type.value}`,
          isNew: true,
          adasLinks: null,
          lookupType: type.value,
          year: year,
          model: model,
          // links typed against this record before it exists, kept here so moving off it
          // and back does not lose them
          stagedLinks: [],
          // what the save is addressed with
          adasChecklistMatrix: this.groupSource.adasChecklistMatrix,
          adasChecklistMatrixVehicle: this.groupSource.adasChecklistMatrixVehicle,
          vehicleMake: this.groupSource.vehicleMake,
          make: this.groupSource.make,
          // the group's matrix range, so the year dropdown still has its options when a
          // staged record is the one selected
          yearStart: this.groupSource.yearStart,
          yearEnd: this.groupSource.yearEnd,
          // display only, and what the reported-system filter reads
          category: this.groupSource.adasChecklistMatrixName,
          system: this.groupSource.system,
          location: this.groupSource.location
        })
      })
      // this.newRecord = { year: '', model: '' }
      this.clearSaveMessage()
    },
    /** Nothing was committed, so a staged record just goes -- as a typed-in link does. */
    removeAddedRecord(row) {
      let position = this.addedRecords.indexOf(row)
      if (position === -1) {
        return
      }
      this.addedRecords.splice(position, 1)
      if (this.selectedPendingKey === row.pendingKey) {
        this.clearRecord()
      }
      this.newRecordMessage = ''
      this.clearSaveMessage()
    },
    /** A stored record is only marked here; it is deleted when this column saves. */
    removeStoredRecord(row) {
      let id = row ? parseInt(row.adasLinks) : 0
      if (!id || this.deletedRecords.indexOf(id) !== -1) {
        return
      }
      this.deletedRecords.push(id)
      // the check is spent once the row is marked, so undoing the removal cannot bring a stale one back
      let checked = this.checkedRecords.indexOf(id)
      if (checked !== -1) {
        this.checkedRecords.splice(checked, 1)
      }
      // its links go with it, so anything staged against them has nowhere to land
      if (parseInt(this.selectedId) === id) {
        this.resetLinkChanges()
      }
      this.clearSaveMessage()
    },
    removeCheckedRecords() {
      // a copy: marking a row drops it out of checkedRemovableRecords mid-loop
      this.checkedRemovableRecords.slice().forEach((row) => this.removeStoredRecord(row))
      this.checkedRecords = []
    },
    restoreRecord(row) {
      let position = this.deletedRecords.indexOf(parseInt(row.adasLinks))
      if (position === -1) {
        return
      }
      this.deletedRecords.splice(position, 1)
      this.clearSaveMessage()
    },
    toggleRecordChecked(row) {
      let id = parseInt(row.adasLinks)
      let position = this.checkedRecords.indexOf(id)
      if (position === -1) {
        this.checkedRecords.push(id)
      } else {
        this.checkedRecords.splice(position, 1)
      }
    },
    /** The check-all box only reaches the rows a removal can act on. */
    checkAllVisibleRecords(isChecked) {
      if (!isChecked) {
        this.checkedRecords = []
        return
      }
      this.checkedRecords = this.removableRecords.map((row) => parseInt(row.adasLinks))
    },
    /**
     * The links payload a staged record is created holding, or null when it holds none.
     * An empty source seeds its own envelope, so there is nothing to read back first.
     */
    buildPendingLinksJson(row) {
      let staged = (row.stagedLinks || []).filter(
        (link) => String(link.url || '').trim() !== '' && String(link.name || '').trim() !== ''
      )
      if (!staged.length) {
        return null
      }
      let changes = staged.map((link) => ({
        originalUrl: '',
        documentTitle: String(link.name || '').trim(),
        newUrl: String(link.url || '').trim(),
        isNew: true,
        isDeleted: false,
        categoryId: link.category
      }))
      let outcome = applyLinkChangesDetailed(null, changes, {
        groupCategoryId: row.adasChecklistMatrixVehicle,
        groupCategoryName: DEFAULT_GROUP_CATEGORY_NAME
      })
      return outcome.json
    },
    /** The panel is open because a vehicle was reported, so the add starts on that vehicle. */
    prefillNewRecord() {
      this.newRecord = {
        // a number, so it matches an option of the year dropdown
        year: parseInt(this.reportedYear) || '',
        // a make-only matrix has no model, so the add starts blank
        model: isMakeMatrix(this.model) ? '' : String(this.reportedModel || '')
        // this.reportedModel ? String(this.reportedModel) : ''
      }
    },
    isChanged(link, edits = this) {
      if (!link) {
        return false
      }
      if (link.isNew || this.isDeleted(link, edits)) {
        return true
      }
      let key = this.linkRef(link)
      let titleChanged =
        edits.documentTitles[key] !== undefined && edits.documentTitles[key].trim() !== String(link.name || link.article || '').trim()
      let urlChanged = edits.editedUrls[key] !== undefined && edits.editedUrls[key].trim() !== String(link.url || '').trim()
      return titleChanged || urlChanged
    },
    /**
     * A record's link edits, the selected one's unless a put-aside record's are given. Its stored
     * links then its added ones -- the same links linkGroups lays out, which a put-aside record
     * has no computed for.
     */
    buildChanges(edits = this) {
      let changes = []
      let stored = _.flatMap(edits.record ? buildLinkGroups(edits.record.links) : [], (group) => group.links || [])
      stored.concat(edits.addedLinks || []).forEach((link) => {
        if (!this.isChanged(link, edits)) {
          return
        }
        let documentTitle = this.documentTitle(link, edits).trim()
        if (link.isNew) {
          changes.push({
            originalUrl: '',
            documentTitle: documentTitle,
            newUrl: String(link.url || '').trim(),
            isNew: true,
            isDeleted: false,
            categoryId: link.category
          })
          return
        }
        if (this.isDeleted(link, edits)) {
          changes.push({ originalUrl: this.linkRef(link), documentTitle: documentTitle, newUrl: '', isNew: false, isDeleted: true })
          return
        }
        let url = this.linkUrl(link, edits).trim()
        changes.push({
          originalUrl: this.linkRef(link),
          documentTitle: documentTitle,
          newUrl: url === String(link.url || '').trim() ? '' : url,
          isNew: false,
          isDeleted: false
        })
      })
      return changes
    },
    /** Just the selected record's link edits -- picking another record reaches this far. */
    resetLinkChanges() {
      this.addedLinks = []
      this.deletedLinks = []
      this.documentTitles = {}
      this.editedUrls = {}
      this.expandedLinks = {}
      this.newLink = { name: '', url: '' }
      this.newLinkMessage = ''
      this.clearSaveMessage()
    },
    /** Everything staged in the panel, links and records alike. */
    resetAllChanges() {
      // the selected record is about to stop existing, so let go of it first
      if (this.selectedPendingKey) {
        this.clearRecord()
      }
      this.resetLinkChanges()
      this.stashedEdits = {}
      this.siblingCache = {}
      this.addedRecords = []
      this.deletedRecords = []
      this.checkedRecords = []
      this.newRecordMessage = ''
      this.prefillNewRecord()
    },
    clearSaveMessage() {
      this.saveMessage = ''
      this.isSaveError = false
    },
    cancelChanges() {
      this.guardAll(() => this.resetAllChanges())
    },
    /** For anything that reloads the panel, which takes the staged records with it. */
    guardAll(action) {
      if (!this.hasChanges) {
        action()
        return
      }
      this.pendingAction = action
      this.showDiscardConfirm = true
    },
    cancelDiscard() {
      this.showDiscardConfirm = false
      this.pendingAction = null
    },
    confirmDiscard() {
      this.showDiscardConfirm = false
      let action = this.pendingAction
      this.pendingAction = null
      this.resetAllChanges()
      if (action) {
        action()
      }
    },
    async save() {
      if (this.isSaving || !this.hasChanges) {
        return
      }
      this.clearSaveMessage()
      let token = this.recordRequestId
      let removedIds = this.deletedRecords.slice()
      let pending = this.addedRecords.slice()
      // the links of a record on its way out have nowhere to be written
      let wasSelectedRemoved = removedIds.indexOf(parseInt(this.selectedId)) !== -1
      let selectedPendingKey = this.selectedPendingKey
      // every record with link edits: the selected one and those moved off, bar any on their way
      // out. A staged record has no id to write links to; they go in the call that creates it
      let edited = Object.values(this.stashedEdits).filter(
        (edits) => removedIds.indexOf(parseInt(edits.record.adasLinks)) === -1 && this.buildChanges(edits).length > 0
      )
      if (!wasSelectedRemoved && this.hasSelectedLinkChanges) {
        edited.unshift(this)
      }
      // every payload is rewritten here, so a record that cannot be read stops the save
      // before anything at all is sent
      let writes = []
      for (const edits of edited) {
        let changes = this.buildChanges(edits)
        let result = applyLinkChangesDetailed(edits.record.links, changes, this.groupOptionsFor(edits.record))
        if (!result.ok) {
          this.saveMessage = `The links held against record #${edits.record.adasLinks} could not be read, so nothing was saved.`
          this.isSaveError = true
          return
        }
        if (result.json !== null) {
          writes.push({ adasLinks: edits.record.adasLinks, outcome: result })
        }
      }
      if (edited.length && !writes.length) {
        this.saveMessage = 'There was nothing to save.'
        this.isSaveError = true
        return
      }
      // what the save message reports, over every record written
      let outcome = writes.length
        ? {
            added: _.sumBy(writes, (write) => write.outcome.added),
            removed: _.sumBy(writes, (write) => write.outcome.removed),
            updated: _.sumBy(writes, (write) => write.outcome.updated),
            skipped: _.flatMap(writes, (write) => write.outcome.skipped)
          }
        : null
      this.isSaving = true
      try {
        // each step clears what it has done, so a failure leaves the rest staged to retry
        if (removedIds.length) {
          let results = await this.removeAdasLinks({ adasLinks: removedIds.map((id) => ({ adasLink: id })) })
          if (results && results.error) {
            this.saveMessage = results.error
            this.isSaveError = true
            return
          }
          this.deletedRecords = []
        }
        // one call per staged row, since each one is a record of its own, and its links
        // ride along -- the save inserts them and points the new record at them
        let duplicates = 0
        let createdSelectedId = null
        for (const row of pending) {
          let results = await this.saveAdasLinks({
            // no id, so the save takes its insert branch
            adasLinks: null,
            adasChecklistMatrix: row.adasChecklistMatrix,
            adasChecklistMatrixVehicle: row.adasChecklistMatrixVehicle,
            vehicleMake: row.vehicleMake,
            model: row.model,
            year: row.year,
            lookupType: row.lookupType,
            links: this.buildPendingLinksJson(row)
          })
          if (results && results.error) {
            this.saveMessage = results.error
            this.isSaveError = true
            return
          }
          // a record that turned up in the meantime is answered with -99, not an error
          if (parseInt(results) === -99) {
            duplicates++
          } else if (row.pendingKey === selectedPendingKey && parseInt(results) > 0) {
            // stay on the record the admin was working on, now that it has an id
            createdSelectedId = parseInt(results)
          }
          this.addedRecords.splice(this.addedRecords.indexOf(row), 1)
        }
        for (const write of writes) {
          let results = await this.saveLinkManage({ adasLinks: write.adasLinks, links: write.outcome.json })
          if (results && results.error) {
            this.saveMessage = results.error
            this.isSaveError = true
            return
          }
        }
        if (token !== this.recordRequestId) {
          return
        }
        // the records are read back rather than patched: a create makes four rows this
        // column never saw, and the listing is the only place they come from
        let keepId = wasSelectedRemoved ? null : createdSelectedId || this.selectedId
        await this.load(keepId)
        this.saveMessage = this.describeSave(outcome, pending.length - duplicates, removedIds.length, duplicates)
        this.isSaveError = duplicates > 0 || !!(outcome && outcome.skipped.length)
      } catch {
        if (token === this.recordRequestId) {
          this.saveMessage = 'The changes could not be saved right now. Please try again.'
          this.isSaveError = true
        }
      } finally {
        this.isSaving = false
      }
    },
    describeSave(outcome, recordsAdded, recordsRemoved, recordsDuplicate) {
      let parts = []
      if (recordsAdded) {
        parts.push(`${recordsAdded} record(s) added`)
      }
      if (recordsRemoved) {
        parts.push(`${recordsRemoved} record(s) removed`)
      }
      if (outcome) {
        if (outcome.added) {
          parts.push(`${outcome.added} link(s) added`)
        }
        if (outcome.removed) {
          parts.push(`${outcome.removed} link(s) removed`)
        }
        if (outcome.updated) {
          parts.push(`${outcome.updated} link(s) updated`)
        }
      }
      let message = `Saved${parts.length ? ' - ' + parts.join(', ') : ''}.`
      if (recordsDuplicate) {
        message = `${message} ${recordsDuplicate} record(s) were already in the matrix and were left alone.`
      }
      if (outcome && outcome.skipped.length) {
        message = `${message} ${outcome.skipped.length} change(s) were skipped.`
      }
      return message
    },
    openPush() {
      if (!this.canOpenPush) {
        return
      }
      this.pushRecords = this.pushableRecords
    },
    closePush() {
      this.pushRecords = null
    },
    /** @param preferredId an AdasLinks id to stay on when the reload still holds it. */
    async load(preferredId) {
      let token = ++this.requestId
      this.isLoading = true
      this.records = []
      this.groupRecord = null
      this.noMatchMessage = ''
      this.closePush()
      this.clearRecord()
      this.resetAllChanges()
      try {
        let found = await findLaunchPadVehicleLinks(this.model || {}, {
          isCurrent: () => token === this.requestId,
          getVehicleMakes: async () => {
            await this.getLookups({ keys: ['VehicleMake'] })
            return this.lookups['VehicleMake'] || []
          },
          getAdasMatrices: this.fetchAdasMatrices,
          getAdasMakeMatrix: this.fetchAdasMakeMatrix
        })
        if (token !== this.requestId) {
          return
        }
        this.records = found.records
        // with no records to select, this is what names the group a new one is added to
        this.groupRecord = found.group || null
        this.noMatchMessage = found.message
        let preferred = preferredId
          ? this.allRecords.find((row) => parseInt(row.adasLinks) === parseInt(preferredId))
          : null
        // the year and the selection are settled together, so a save comes back to the record it
        // kept rather than to one the year is filtering out
        if (preferred) {
          this.newRecord.year = parseInt(preferred.year) || ''
        }
        this.selectRecord(preferred || this.visibleRecords[0] || this.allRecords[0] || null)
      } catch {
        if (token === this.requestId) {
          this.noMatchMessage = 'The LaunchPad matrix could not be read right now. Please try again.'
        }
      } finally {
        if (token === this.requestId) {
          this.isLoading = false
        }
      }
    },
    clearRecord() {
      this.recordRequestId++
      this.selectedId = null
      this.selectedPendingKey = null
      this.record = null
      this.expandedGroups = []
      this.isLoadingRecord = false
      this.resetLinkChanges()
    },
    /** Keeps the links pane on a record the year filter still shows. */
    selectVisibleRecord() {
      // a load or save settles the selection itself
      if (this.isLoading || this.isSaving) {
        return
      }
      if (!this.visibleRecords.length) {
        // nothing for this year; its edits are put aside, as moving to another record does
        this.stashSelectedEdits()
        this.clearRecord()
        return
      }
      if (this.visibleRecords.some((row) => this.isSelected(row))) {
        return
      }
      this.selectRecord(this.visibleRecords[0])
    },
    chooseRecord(row) {
      // a save in flight reloads the panel when it lands; moving now would leave its edits staged
      if (this.isSaving || this.isSelected(row)) {
        return
      }
      this.selectRecord(row)
    },
    groupOptionsFor(record) {
      return {
        groupCategoryId: record ? record.adasChecklistMatrixVehicle : null,
        groupCategoryName: (record && record.adasChecklistMatrixName) || ''
      }
    },
    /** Puts the selected record's link edits aside, so moving to another record keeps them. */
    stashSelectedEdits() {
      let id = parseInt(this.selectedId)
      if (!id || !this.record || this.isPendingSelected) {
        return
      }
      if (this.buildChanges().length) {
        this.stashedEdits[id] = {
          record: this.record,
          addedLinks: this.addedLinks,
          deletedLinks: this.deletedLinks,
          documentTitles: this.documentTitles,
          editedUrls: this.editedUrls
        }
      } else {
        delete this.stashedEdits[id]
      }
    },
    hasUnsavedLinks(row) {
      if (!row || !row.adasLinks) {
        return false
      }
      let stashed = this.stashedEdits[parseInt(row.adasLinks)]
      return this.isSelected(row) ? this.hasSelectedLinkChanges : !!stashed && this.buildChanges(stashed).length > 0
    },
    /** The selected record's other link types, the ones its link changes are staged on. */
    selectedSiblings() {
      return this.siblingRecords(this.selectedRow(), this.deletedRecords)
    },
    /** The selected listing row, a staged one included. */
    selectedRow() {
      if (this.isPendingSelected) {
        return this.selectedPendingRecord
      }
      return this.records.find((item) => parseInt(item.adasLinks) === parseInt(this.selectedId)) || null
    },
    /** The staged records under the selected one's other link types; they hold their links on themselves. */
    pendingSiblings(selected) {
      if (!selected) {
        return []
      }
      return this.addedRecords.filter(
        (row) =>
          row !== selected &&
          parseInt(row.lookupType) !== parseInt(selected.lookupType) &&
          parseInt(row.adasChecklistMatrixVehicle) === parseInt(selected.adasChecklistMatrixVehicle) &&
          parseInt(row.year) === parseInt(selected.year) &&
          normalize(row.model) === normalize(selected.model)
      )
    },
    /** Those that have loaded, as their staged edits -- set up on first use. */
    siblingEditsList() {
      let list = []
      this.selectedSiblings().forEach((sibling) => {
        let id = parseInt(sibling.adasLinks)
        if (!this.stashedEdits[id]) {
          if (!this.siblingCache[id]) {
            return
          }
          this.stashedEdits[id] = { record: this.siblingCache[id], addedLinks: [], deletedLinks: [], documentTitles: {}, editedUrls: {} }
        }
        // read back, so the changes go through the reactive copy
        list.push(this.stashedEdits[id])
      })
      // a staged record has only added links, so its staged list is the whole of what can change
      this.pendingSiblings(this.selectedRow()).forEach((row) => {
        if (!Array.isArray(row.stagedLinks)) {
          row.stagedLinks = []
        }
        list.push({ record: null, addedLinks: row.stagedLinks, deletedLinks: [], documentTitles: {}, editedUrls: {} })
      })
      return list
    },
    holdsLink(record, key) {
      return (record ? buildLinkGroups(record.links) : []).some((group) => (group.links || []).some((link) => this.linkRef(link) === key))
    },
    mirrorEdit(key, field, value) {
      this.siblingEditsList().forEach((edits) => {
        if (this.holdsLink(edits.record, key)) {
          edits[field][key] = value
        }
      })
    },
    mirrorAdd(link) {
      let url = String(link.url || '').trim()
      this.siblingEditsList().forEach((edits) => {
        let already =
          edits.addedLinks.some((added) => added.mirrorKey === link.mirrorKey || this.linkRef(added) === url) || this.holdsLink(edits.record, url)
        if (already) {
          return
        }
        let copy = buildManualLink(link.name, link.url, link.category)
        copy.mirrorKey = link.mirrorKey
        edits.addedLinks.push(copy)
      })
    },
    mirrorAddedChange(link, fields) {
      this.siblingEditsList().forEach((edits) => {
        edits.addedLinks.filter((added) => added.mirrorKey === link.mirrorKey).forEach((added) => Object.assign(added, fields))
      })
    },
    mirrorRemoveAdded(link) {
      this.siblingEditsList().forEach((edits) => {
        // in place: a staged record's list is its own, not a copy
        edits.addedLinks
          .filter((added) => added.mirrorKey === link.mirrorKey)
          .forEach((added) => edits.addedLinks.splice(edits.addedLinks.indexOf(added), 1))
      })
    },
    mirrorRemove(ref, isRemoved) {
      this.siblingEditsList().forEach((edits) => {
        if (!this.holdsLink(edits.record, ref)) {
          return
        }
        let position = edits.deletedLinks.indexOf(ref)
        if (isRemoved && position === -1) {
          edits.deletedLinks.push(ref)
        } else if (!isRemoved && position !== -1) {
          edits.deletedLinks.splice(position, 1)
        }
      })
    },
    /** Loads the selected record's other link types, then stages on them anything done before they arrived. */
    async loadSiblings(token) {
      for (const sibling of this.selectedSiblings()) {
        let id = parseInt(sibling.adasLinks)
        if (this.siblingCache[id] || this.stashedEdits[id]) {
          continue
        }
        let found = await this.getLinkManage(id)
        if (token !== this.recordRequestId) {
          return
        }
        if (found) {
          this.siblingCache[id] = found
        }
      }
      Object.keys(this.documentTitles).forEach((key) => this.mirrorEdit(key, 'documentTitles', this.documentTitles[key]))
      Object.keys(this.editedUrls).forEach((key) => this.mirrorEdit(key, 'editedUrls', this.editedUrls[key]))
      this.addedLinks.forEach((link) => this.mirrorAdd(link))
      this.deletedLinks.forEach((ref) => this.mirrorRemove(ref, true))
    },
    /**
     * A staged record has nothing to fetch, so its record is built here from what the add
     * cloned. Its links are held on the staged record itself rather than in the per-record
     * staging, so moving off it and back keeps them.
     */
    selectPendingRecord(row) {
      this.stashSelectedEdits()
      // anything still in flight for the last record must not land on this one
      this.recordRequestId++
      this.selectedId = null
      this.selectedPendingKey = row.pendingKey
      this.expandedGroups = []
      this.isLoadingRecord = false
      this.resetLinkChanges()
      this.record = {
        adasLinks: null,
        isNew: true,
        adasChecklistMatrix: row.adasChecklistMatrix,
        adasChecklistMatrixVehicle: row.adasChecklistMatrixVehicle,
        adasChecklistMatrixName: row.category,
        vehicleMake: row.vehicleMake,
        make: row.make,
        year: row.year,
        model: row.model,
        lookupType: row.lookupType,
        yearStart: row.yearStart,
        yearEnd: row.yearEnd,
        system: row.system,
        location: row.location,
        links: null
      }
      if (!Array.isArray(row.stagedLinks)) {
        row.stagedLinks = []
      }
      // the same array, so addLink and removeAddedLink write straight to the staged record
      this.addedLinks = row.stagedLinks
      this.loadSiblings(this.recordRequestId)
    },
    async selectRecord(row) {
      if (!row) {
        this.clearRecord()
        return
      }
      if (this.isSelected(row)) {
        return
      }
      if (this.isPendingRecord(row)) {
        this.selectPendingRecord(row)
        return
      }
      this.stashSelectedEdits()
      let token = ++this.recordRequestId
      this.selectedId = row.adasLinks
      this.selectedPendingKey = null
      this.record = null
      this.expandedGroups = []
      this.resetLinkChanges()
      // coming back to a record put aside: its edits, against the record they were made on
      let stashed = this.stashedEdits[parseInt(row.adasLinks)]
      if (stashed) {
        delete this.stashedEdits[parseInt(row.adasLinks)]
        this.record = stashed.record
        this.addedLinks = stashed.addedLinks
        this.deletedLinks = stashed.deletedLinks
        this.documentTitles = stashed.documentTitles
        this.editedUrls = stashed.editedUrls
        this.isLoadingRecord = false
        if (this.linkGroups.length === 1) {
          this.expandedGroups = [0]
        }
        this.loadSiblings(token)
        return
      }
      this.isLoadingRecord = true
      try {
        let found = await this.getLinkManage(row.adasLinks)
        if (token !== this.recordRequestId) {
          return
        }
        this.record = found
        if (this.linkGroups.length === 1) {
          this.expandedGroups = [0]
        }
        this.loadSiblings(token)
      } catch {
        if (token === this.recordRequestId) {
          this.record = null
        }
      } finally {
        if (token === this.recordRequestId) {
          this.isLoadingRecord = false
        }
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-launch-pad {
  width: 100%;
}
.custom-header {
  align-items: center;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.5rem;
}
.custom-body {
  overflow-x: hidden;
}
.custom-refresh {
  color: #6c757d;
  margin-left: 0.5rem;
  cursor: pointer;
}
.custom-refresh:hover {
  color: #0d6efd;
}
.custom-info-title {
  font-weight: 600;
  padding: 0.375rem 0.75rem;
}
.custom-info-row {
  margin: 0;
}
.custom-info-row + .custom-info-row {
  border-top: 1px solid #dee2e6;
}
.custom-info-row > div {
  padding: 0.375rem 0.75rem;
  word-break: break-word;
}
.custom-info-icon {
  color: #6c757d;
  margin-right: 0.5rem;
}
.custom-tab-panel {
  padding-top: 1rem;
  margin: 10px;
}
.custom-collapse-header {
  cursor: pointer;
  padding: 0.5rem 0;
  user-select: none;
}
.custom-link-ids-wrapper {
  overflow-x: hidden;
  overflow-y: auto;
  padding: 5px 0;
  border: 1px solid #dee2e6;
}
.custom-link-ids {
  list-style-type: none;
  padding: 0;
  margin: 0;
}
.custom-link-id {
  padding: 5px;
  font-weight: 500;
  border: 1px solid #dee2e6;
  margin: 5px;
  cursor: pointer;
  word-break: break-word;
}
.custom-link-id:hover {
  background: rgba(255, 251, 0, 0.1);
}
.custom-link-id-selected {
  background: rgba(255, 251, 0, 0.5);
}
.custom-link-id-new {
  border-color: #7cb342;
}
// after the selected and reported rules, so a row on its way out reads as one whatever else it is
.custom-link-id-removed {
  background: #fdeaea;
  border-color: #d9534f;
}
.custom-link-check {
  cursor: pointer;
  vertical-align: middle;
}
.custom-link-id-detail {
  font-size: 0.8em;
  font-weight: 400;
  color: #6c757d;
}
.custom-year-model {
  font-size: 0.8em;
}
.custom-master-link {
  padding: 0.375rem 0;
}
.custom-master-link + .custom-master-link {
  border-top: 1px solid #dee2e6;
}
.custom-reported-link {
  background-color: #fdeaea;
}
.custom-row-actions {
  cursor: default;
  white-space: nowrap;
}
.custom-row-actions .btn-link {
  padding: 0 0.25rem;
  text-decoration: none;
}
.custom-changed-link {
  border: 1px solid #7cb342 !important;
}
.custom-deleted-link {
  background-color: #fdeaea;
  border: 1px solid #d9534f !important;
}
.custom-deleted-title {
  text-decoration: line-through;
  opacity: 0.7;
}
.custom-save-bar {
  border-top: 1px solid #dee2e6;
  padding-top: 0.5rem;
}
</style>
